import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AuthAlert, AuthPageLayout } from '../../auth/components/AuthPageLayout';
import {
  clearPickerSession,
  readPickerSession,
  selectWorkspace,
} from '../services/workspaceService';
import type { WorkspaceSummary } from '../types/workspace';
import { followWorkspaceSelection } from '../workspaceNavigation';
import styles from './WorkspacePicker.module.css';

const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const initialsOf = (workspace: WorkspaceSummary): string => {
  const source = workspace.name.trim() || workspace.slug.trim();
  return source.slice(0, 2) || '?';
};

export const WorkspacePickerPage: React.FC = () => {
  const { t } = useTranslation('workspaces');
  const navigate = useNavigate();
  const session = React.useMemo(() => readPickerSession(), []);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingTenantId, setPendingTenantId] = React.useState<string | null>(null);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const onSelect = async (workspace: WorkspaceSummary) => {
    setError(null);
    setPendingTenantId(workspace.tenantId);

    try {
      const selection = await selectWorkspace(session.token, workspace.tenantId);
      followWorkspaceSelection(selection, navigate);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.selectFailed'));
      setPendingTenantId(null);
    }
  };

  const onUseAnotherAccount = () => {
    clearPickerSession();
    navigate('/login', { replace: true });
  };

  return (
    <AuthPageLayout
      title={t('picker.title')}
      subtitle={t('picker.subtitle', { email: session.username })}
    >
      {error ? <AuthAlert variant="error">{error}</AuthAlert> : null}

      {session.workspaces.length === 0 ? (
        <p className={styles.status}>{t('picker.empty')}</p>
      ) : (
        <div className={styles.list} role="list">
          {session.workspaces.map(workspace => (
            <button
              key={workspace.tenantId}
              type="button"
              role="listitem"
              className={styles.workspace}
              onClick={() => void onSelect(workspace)}
              disabled={pendingTenantId !== null}
            >
              <span className={styles.avatar} aria-hidden="true">{initialsOf(workspace)}</span>
              <span className={styles.details}>
                <span className={styles.name}>{workspace.name || workspace.slug}</span>
                <span className={styles.host}>{hostOf(workspace.url)}</span>
              </span>
              {workspace.requiresPasswordChange ? (
                <span className={styles.badge}>{t('picker.passwordChangeRequired')}</span>
              ) : (
                <span className={styles.chevron} aria-hidden="true">
                  {pendingTenantId === workspace.tenantId ? '…' : '›'}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {session.isPlatformOperator ? (
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => navigate('/operator')}
          disabled={pendingTenantId !== null}
        >
          {t('picker.allWorkspaces')}
        </button>
      ) : null}

      {session.accountToken ? (
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => navigate('/workspaces/new')}
          disabled={pendingTenantId !== null}
        >
          {t('picker.createWorkspace')}
        </button>
      ) : null}

      <p className={styles.footer}>
        <span>{t('picker.notYou')}</span>
        <button type="button" className={styles.linkButton} onClick={onUseAnotherAccount}>
          {t('picker.useAnotherAccount')}
        </button>
      </p>
    </AuthPageLayout>
  );
};

export default WorkspacePickerPage;
